import { createFileRoute } from '@tanstack/react-router'

import { useCreateCompany } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/companies/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Companies.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewCompanyForm />
		</RouteModal>
	)
}

function NewCompanyForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()

	const createCompany = useCreateCompany()

	const form = useAppForm({
		defaultValues: {
			name: '',
			email: '',
			phone: '',
			orgNumber: '',
			externalId: ''
		},
		onSubmit: async ({ value }) => {
			const result = await createCompany({
				body: {
					name: value.name,
					email: value.email || undefined,
					phone: value.phone || undefined,
					orgNumber: value.orgNumber || undefined,
					externalId: value.externalId || undefined
				}
			})
			createdToast(L.Companies.CreateSuccess(), {
				to: '/companies/$companyUuid',
				params: { companyUuid: result.response.uuid }
			})
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Companies.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Companies.Create.NamePlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='email'
							children={field => (
								<field.TextField
									label={L.Fields.Email()}
									placeholder={L.Companies.Edit.EmailPlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='phone'
							children={field => (
								<field.TextField
									label={L.Fields.Phone()}
									placeholder={L.Companies.Edit.PhonePlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='orgNumber'
							children={field => (
								<field.TextField
									label={L.Fields.OrgNumber()}
									placeholder={L.Companies.Edit.OrgNumberPlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='externalId'
							children={field => (
								<field.TextField
									label={L.Fields.ExternalId()}
									placeholder={L.Companies.Create.ExternalIdPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
